<script lang="ts" setup>
import type { ComputedRef } from 'vue'
import { get } from 'lodash-es'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  COLLECTION_USER_CENTER_HOME_PATH,
  getUserCenterRedirectMapStoreInfo,
  getUserCenterSiderMenusConfigStoreInfo,
  isCollectionUserCenterStoreInfo,
  isExternalRedirectPath,
  MALL_USER_CENTER_HOME_PATH,
  resolveUserCenterRedirectPath,
} from '../../utils/collectionUserCenter'
import newCustomerTaskBanner from '../src/assets/img/new-customer-task-banner.png'
import { mallHomeSidebarIconSrcMap } from '../src/constants/mallHomeFigmaIcons'
import i18n, { $t, isLongLocale } from '../src/utils/i18n'
import { isNewCustomerTaskEnabled } from '../src/utils/newCustomerTask'

type MallMenuQueryMap = Record<string, string[]>

interface MallMenuItem {
  key: string
  name: string
  link: string
  icon?: string
  activePaths?: string[]
  activeQueries?: MallMenuQueryMap
  children?: MallMenuItem[]
}

interface FlatMenuItem extends MallMenuItem {
  depth: number
  parentKey?: string
}

interface MallMenuGroup {
  title: string
  items: MallMenuItem[]
}

type CustomSiderMenuConfig = Record<string, any>

const sidebarLocaleCopyMap: Record<string, {
  businessCenter: string
  defaultMember: string
  memberLevelPrefix: string
  personalCenter: string
  rechargePrefix: string
  userAssets: string
}> = {
  'en': {
    businessCenter: 'Business Center',
    defaultMember: 'Standard Member',
    memberLevelPrefix: 'Member Level',
    personalCenter: 'Profile',
    rechargePrefix: 'Recharge',
    userAssets: 'My Assets',
  },
  'ja_JP': {
    businessCenter: 'サービス',
    defaultMember: '一般会員',
    memberLevelPrefix: '会員レベル',
    personalCenter: 'マイページ',
    rechargePrefix: 'チャージ',
    userAssets: '資産',
  },
  'vi_VN': {
    businessCenter: 'Trung tâm dịch vụ',
    defaultMember: 'Thành viên thường',
    memberLevelPrefix: 'Cấp độ thành viên',
    personalCenter: 'Trung tâm cá nhân',
    rechargePrefix: 'Nạp',
    userAssets: 'Tài sản của tôi',
  },
  'zh-CN': {
    businessCenter: '业务中心',
    defaultMember: '普通会员',
    memberLevelPrefix: '会员等级',
    personalCenter: '个人中心',
    rechargePrefix: '去充值',
    userAssets: '我的资产',
  },
  'zh-TW': {
    businessCenter: '業務中心',
    defaultMember: '普通會員',
    memberLevelPrefix: '會員等級',
    personalCenter: '個人中心',
    rechargePrefix: '去充值',
    userAssets: '我的資產',
  },
}

const collectionSidebarLocaleCopyMap: Record<string, {
  coupons: string
  welfareCenter: string
  forecast: string
  home: string
  income: string
  orders: string
  package: string
  points: string
  proxy: string
  title: string
  address: string
}> = {
  'en': {
    coupons: 'Coupons',
    welfareCenter: 'Benefits Center',
    forecast: 'Forecast Package',
    home: 'Consolidation Home',
    income: 'Fee Records',
    orders: 'My Orders',
    package: 'My Packages',
    points: 'Points Center',
    proxy: 'Agent Promotion',
    title: 'Consolidation',
    address: 'Receiving Address',
  },
  'ja_JP': {
    coupons: 'クーポン',
    welfareCenter: '特典センター',
    forecast: '荷物予報',
    home: '集運ホーム',
    income: '費用明細',
    orders: '私の注文',
    package: '私の荷物',
    points: 'ポイントセンター',
    proxy: '代理プロモーション',
    title: '集運',
    address: '配送先住所',
  },
  'vi_VN': {
    coupons: 'Phiếu giảm giá',
    welfareCenter: 'Trung tâm phúc lợi',
    forecast: 'Dự báo kiện hàng',
    home: 'Trang chủ gom hàng',
    income: 'Lịch sử phí',
    orders: 'Đơn hàng của tôi',
    package: 'Kiện hàng của tôi',
    points: 'Trung tâm điểm',
    proxy: 'Quảng bá đại lý',
    title: 'Gom hàng',
    address: 'Địa chỉ giao hàng',
  },
  'zh-CN': {
    coupons: '优惠券',
    welfareCenter: '福利中心',
    forecast: '预报包裹',
    home: '集运首页',
    income: '费用流水',
    orders: '我的订单',
    package: '我的包裹',
    points: '积分中心',
    proxy: '代理推广',
    title: '集运中心',
    address: '收货地址',
  },
  'zh-Hans': {
    coupons: '优惠券',
    welfareCenter: '福利中心',
    forecast: '预报包裹',
    home: '集运首页',
    income: '费用流水',
    orders: '我的订单',
    package: '我的包裹',
    points: '积分中心',
    proxy: '代理推广',
    title: '集运中心',
    address: '收货地址',
  },
  'zh-Hant': {
    coupons: '優惠券',
    welfareCenter: '福利中心',
    forecast: '預報包裹',
    home: '集運首頁',
    income: '費用流水',
    orders: '我的訂單',
    package: '我的包裹',
    points: '積分中心',
    proxy: '代理推廣',
    title: '集運中心',
    address: '收貨地址',
  },
  'zh-TW': {
    coupons: '優惠券',
    welfareCenter: '福利中心',
    forecast: '預報包裹',
    home: '集運首頁',
    income: '費用流水',
    orders: '我的訂單',
    package: '我的包裹',
    points: '積分中心',
    proxy: '代理推廣',
    title: '集運中心',
    address: '收貨地址',
  },
}

const router = useRouter()
const route = useRoute()
const store = useStore()
const sidebarScrollbarRef = ref<any>(null)

const SIDEBAR_SCROLL_STORAGE_KEY = 'user_center_shell_sidebar_scroll_top'
let sidebarScrollTopMemory = 0

const currentLocale: ComputedRef<string> = computed(() => {
  return String(get(i18n, 'global.locale.value') || 'zh-CN')
})

const sidebarCopy = computed(() => {
  return sidebarLocaleCopyMap[currentLocale.value]
    || sidebarLocaleCopyMap['zh-CN']
})

const collectionSidebarCopy = computed(() => {
  return collectionSidebarLocaleCopyMap[currentLocale.value]
    || collectionSidebarLocaleCopyMap['zh-Hant']
})

const isLongTextLocale = computed(() => {
  return isLongLocale(currentLocale.value)
})

const hiddenSider = computed(() => Boolean(route.meta.hiddenSider))

const isFullLayout = computed(() => Boolean(route.meta.fullLayout))

const asideWidth = computed(() => (isLongTextLocale.value ? '244px' : '220px'))

const storeInfoData = computed(() => {
  return get(store, 'state.app.storeInfoData', {}) || {}
})

const isCollectionUserCenter = computed(() => {
  return isCollectionUserCenterStoreInfo(storeInfoData.value)
})

const userCenterSiderMenusConfig = computed(() => {
  return getUserCenterSiderMenusConfigStoreInfo(storeInfoData.value)
})

const userCenterRedirectMap = computed(() => {
  return getUserCenterRedirectMapStoreInfo(storeInfoData.value)
})

const hasCustomSiderMenusConfig = computed(() => userCenterSiderMenusConfig.value.length > 0)
const isNewCustomerTaskShop = computed(() => isNewCustomerTaskEnabled())
const showCollectionTaskBanner = computed(() => (
  isCollectionUserCenter.value
  && !hasCustomSiderMenusConfig.value
  && isNewCustomerTaskShop.value
))

const collectionUserMenuItems = computed<MallMenuItem[]>(() => [
  {
    key: 'collection-home',
    name: collectionSidebarCopy.value.home,
    link: COLLECTION_USER_CENTER_HOME_PATH,
    icon: 'icon-home_index',
  },
  {
    key: 'collection-package',
    name: collectionSidebarCopy.value.package,
    link: '/collection/package',
    icon: 'icon-parcel',
  },
  {
    key: 'collection-order',
    name: collectionSidebarCopy.value.orders,
    link: '/collection/order',
    icon: 'icon-order',
    activePaths: ['/collection/payOrder'],
  },
  {
    key: 'collection-forecast',
    name: collectionSidebarCopy.value.forecast,
    link: '/collection/forecast',
    icon: 'icon-submitParcels',
  },
  {
    key: 'collection-proxy',
    name: collectionSidebarCopy.value.proxy,
    link: '/collection/proxy',
    icon: 'icon-Affiliates',
    activePaths: ['/collection/invite', '/collection/proxy', '/collection/withdrawCash'],
  },
  {
    key: 'collection-points',
    name: collectionSidebarCopy.value.points,
    link: '/collection/points',
    icon: 'icon-home_me',
  },
  {
    key: 'collection-coupons',
    name: collectionSidebarCopy.value.coupons,
    link: '/collection/coupons?status=1',
    icon: 'icon-coupon',
    activePaths: ['/collection/coupons'],
  },
  {
    key: 'collection-income',
    name: collectionSidebarCopy.value.income,
    link: '/collection/income',
    icon: 'icon-Wallet',
  },
  {
    key: 'business-address',
    name: collectionSidebarCopy.value.address,
    link: '/collection/address',
    icon: 'icon-spiritling-dingwei',
  },
])

function resolveLocaleText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ''
  }

  const valueMap = value as Record<string, unknown>
  const locale = currentLocale.value
  const localeAliases = [
    locale,
    locale.replace(/-/g, '_'),
    locale.replace(/_/g, '-'),
    locale.startsWith('zh') && locale.toLowerCase().includes('tw') ? 'zh-Hant' : '',
    locale.startsWith('zh') && !locale.toLowerCase().includes('tw') ? 'zh-Hans' : '',
    'zh-TW',
    'zh-CN',
    'en',
  ].filter(Boolean)

  for (const key of localeAliases) {
    const text = valueMap[key]
    if (typeof text === 'string' || typeof text === 'number') {
      return String(text)
    }
  }

  const firstText = Object.values(valueMap).find((text) => {
    return typeof text === 'string' || typeof text === 'number'
  })

  return firstText === undefined ? '' : String(firstText)
}

function resolveConfigTitle(config: CustomSiderMenuConfig): string {
  const titleKey = get(config, 'titleKey') || get(config, 'labelKey') || get(config, 'nameKey')
  if (titleKey) {
    return $t(String(titleKey))
  }

  return resolveLocaleText(
    get(config, 'title')
    ?? get(config, 'name')
    ?? get(config, 'label')
    ?? get(config, 'text'),
  )
}

function normalizeConfigPath(path: unknown): string {
  if (typeof path === 'string' || typeof path === 'number') {
    return String(path).trim()
  }

  return ''
}

function normalizeConfigStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || '').trim())
      .filter(Boolean)
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const stringValue = String(value).trim()
    return stringValue ? [stringValue] : []
  }

  return []
}

function normalizeConfigActiveQueries(value: unknown): MallMenuQueryMap | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  const activeQueries = Object.entries(value as Record<string, unknown>).reduce<MallMenuQueryMap>((result, [key, rawValue]) => {
    const values = normalizeConfigStringList(rawValue)
    if (values.length) {
      result[key] = values
    }

    return result
  }, {})

  return Object.keys(activeQueries).length ? activeQueries : undefined
}

function getConfigChildren(value: unknown): CustomSiderMenuConfig[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is CustomSiderMenuConfig => {
    return Boolean(item && typeof item === 'object' && !Array.isArray(item))
  })
}

function normalizeCustomMenuItem(config: CustomSiderMenuConfig, index: number, parentKey: string): MallMenuItem | null {
  const children = getConfigChildren(get(config, 'children'))
    .map((child, childIndex) => normalizeCustomMenuItem(child, childIndex, `${parentKey}-${index}`))
    .filter((item): item is MallMenuItem => Boolean(item))
  const key = String(
    get(config, 'key')
    || get(config, 'code')
    || get(config, 'id')
    || `${parentKey}-${index}`,
  )
  const name = resolveConfigTitle(config)
  const link = normalizeConfigPath(
    get(config, 'link')
    ?? get(config, 'path')
    ?? get(config, 'to')
    ?? get(config, 'url'),
  )
  const icon = String(get(config, 'icon') || '').trim()
  const activePaths = normalizeConfigStringList(get(config, 'activePaths'))
  const activeQueries = normalizeConfigActiveQueries(get(config, 'activeQueries'))

  if (!name && !link && !children.length) {
    return null
  }

  return {
    key,
    name,
    link,
    ...(icon ? { icon } : {}),
    ...(activePaths.length ? { activePaths } : {}),
    ...(activeQueries ? { activeQueries } : {}),
    ...(children.length ? { children } : {}),
  }
}

function isCustomMenuGroup(config: CustomSiderMenuConfig): boolean {
  return Array.isArray(get(config, 'items')) || Array.isArray(get(config, 'menus'))
}

const customSiderMenuGroups = computed<MallMenuGroup[]>(() => {
  const flatItems: MallMenuItem[] = []
  const groups: MallMenuGroup[] = []

  userCenterSiderMenusConfig.value.forEach((rawConfig, index) => {
    if (!rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) {
      return
    }

    const config = rawConfig as CustomSiderMenuConfig

    if (isCustomMenuGroup(config)) {
      const rawItems = getConfigChildren(get(config, 'items')).length
        ? getConfigChildren(get(config, 'items'))
        : getConfigChildren(get(config, 'menus'))
      const items = rawItems
        .map((item, itemIndex) => normalizeCustomMenuItem(item, itemIndex, `custom-group-${index}`))
        .filter((item): item is MallMenuItem => Boolean(item))

      if (items.length) {
        groups.push({
          title: resolveConfigTitle(config),
          items,
        })
      }

      return
    }

    const item = normalizeCustomMenuItem(config, index, 'custom')
    if (item) {
      flatItems.push(item)
    }
  })

  if (flatItems.length) {
    groups.unshift({
      title: '',
      items: flatItems,
    })
  }

  return groups
})

const mallUserMenuGroups = computed<MallMenuGroup[]>(() => {
  if (hasCustomSiderMenusConfig.value) {
    return customSiderMenuGroups.value
  }

  if (isCollectionUserCenter.value) {
    return [
      {
        title: collectionSidebarCopy.value.title,
        items: collectionUserMenuItems.value,
      },
    ]
  }

  return [
    {
      title: sidebarCopy.value.personalCenter,
      items: [
        {
          key: 'profile-home',
          name: $t('text.userCenter'),
          link: '/mall/home',
          icon: 'icon-menu-user-center',
        },
        {
          key: 'profile-history',
          name: $t('text.browseHistory'),
          link: '/mall/browseHistory',
          icon: 'icon-time',
        },
        {
          key: 'profile-favorites',
          name: $t('userCenter.menu.favorites'),
          link: '/mall/favorites',
          icon: 'icon-collect2',
        },
        {
          key: 'profile-assets',
          name: sidebarCopy.value.userAssets,
          link: '/mall/recharge',
          icon: 'icon-Wallet',
          activePaths: [
            '/collection/coupons',
            '/collection/points',
            '/collection/recharge',
            '/mall/recharge/gold',
          ],
          children: [
            {
              key: 'profile-assets-balance',
              name: $t('text.wallet'),
              link: '/mall/recharge',
              activePaths: ['/collection/recharge'],
            },
            {
              key: 'profile-assets-points',
              name: $t('userCenter.menu.points'),
              link: '/collection/points',
            },
            {
              key: 'profile-assets-coupons',
              name: $t('userCenter.menu.coupons'),
              link: '/collection/coupons?status=1',
              activePaths: ['/collection/coupons'],
            },
            {
              key: 'profile-assets-gold',
              name: $t('userCenter.home.mallGoldLabel'),
              link: '/mall/recharge/gold',
            },
          ],
        },
      ],
    },
    {
      title: sidebarCopy.value.businessCenter,
      items: [
        {
          key: 'business-orders',
          name: $t('userCenter.menu.myOrders'),
          link: '/mall/platformAllOrderListV2',
          icon: 'icon-order',
          activePaths: ['/mall/unifiedOrderDetail'],
          children: [
            {
              key: 'business-orders-wait-pay',
              name: $t('userCenter.menu.pendingPayment'),
              link: '/mall/platformAllOrderListV2?displayStatus=WAITING_PAY',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['WAITING_PAY'],
              },
            },
            {
              key: 'business-orders-purchased',
              name: $t('userCenter.alibabaOrderList.tabPurchased'),
              link: '/mall/platformAllOrderListV2?displayStatus=PURCHASED',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['PURCHASED'],
              },
            },
            {
              key: 'business-orders-wait-inbound',
              name: $t('userCenter.menu.pendingInbound'),
              link: '/mall/platformAllOrderListV2?displayStatus=WAITING_INBOUND',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['WAITING_INBOUND'],
              },
            },
            {
              key: 'business-orders-completed',
              name: $t('userCenter.menu.completed'),
              link: '/mall/platformAllOrderListV2?displayStatus=COMPLETED',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['COMPLETED'],
              },
            },
            {
              key: 'business-orders-closed',
              name: $t('userCenter.menu.closed'),
              link: '/mall/platformAllOrderListV2?displayStatus=CLOSED',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['CLOSED'],
              },
            },
            {
              key: 'business-orders-cancelled',
              name: $t('userCenter.menu.cancelled'),
              link: '/mall/platformAllOrderListV2?displayStatus=CANCELLED',
              activePaths: ['/mall/platformAllOrderListV2'],
              activeQueries: {
                displayStatus: ['CANCELLED'],
              },
            },
          ],
        },
        {
          key: 'business-warehouse',
          name: $t('userCenter.menu.myWarehouse'),
          link: '/collection/myWarehouse',
          icon: 'icon-Warehouse',
          children: [
            {
              key: 'business-warehouse-pending',
              name: $t('userCenter.menu.pendingInbound'),
              link: '/collection/myWarehouse?statsGroupCode=TOBE_RECEIVED',
              activePaths: ['/collection/myWarehouse'],
              activeQueries: {
                statsGroupCode: ['TOBE_RECEIVED'],
              },
            },
            {
              key: 'business-warehouse-inbound',
              name: $t('userCenter.menu.warehoused'),
              link: '/collection/myWarehouse?statsGroupCode=TOBE_ORDERED',
              activePaths: ['/collection/myWarehouse'],
              activeQueries: {
                statsGroupCode: ['TOBE_ORDERED'],
              },
            },
          ],
        },
        {
          key: 'business-package',
          name: $t('userCenter.menu.myPackage'),
          link: '/collection/myPackage',
          icon: 'icon-parcel',
          activePaths: ['/collection/order'],
          children: [
            {
              key: 'business-package-forwarding',
              name: $t('userCenter.menu.myForwarding'),
              link: '/collection/myPackage?tab=SHIPPED',
              activePaths: ['/collection/myWarehouse'],
              activeQueries: {
                statsGroupCode: ['TOBE_ORDERED'],
              },
            },
          ],
        },
        {
          key: 'business-expert-purchase',
          name: $t('userCenter.menu.expertPurchase'),
          link: '/shop/expertPurchaseHome',
          icon: 'icon-priority-purchase',
          activePaths: [
            '/shop/expertPurchaseHome',
            '/mall/expertPurchaseOrders',
          ],
          children: [
            {
              key: 'business-expert-purchase-home',
              name: $t('userCenter.menu.expertPurchase'),
              link: '/shop/expertPurchaseHome',
              activePaths: ['/shop/expertPurchaseHome'],
            },
            {
              key: 'business-expert-purchase-orders',
              name: $t('userCenter.titles.expertPurchaseOrders'),
              link: '/mall/expertPurchaseOrders',
              activePaths: ['/mall/expertPurchaseOrders'],
            },
          ],
        },
        {
          key: 'business-refund',
          name: $t('userCenter.menu.refundAfterSales'),
          link: '/mall/refundOrderList',
          icon: 'icon-order-coupon',
        },
        {
          key: 'business-invite',
          name: $t('userCenter.menu.inviteRewards'),
          link: '/collection/proxy',
          icon: 'icon-liwuhuodong',
          activePaths: ['/collection/proxy'],
        },
        {
          key: 'business-feedback',
          name: $t('userCenter.menu.feedback'),
          link: '/collection/feedback',
          icon: 'icon-question1',
        },
        {
          key: 'business-address',
          name: $t('userCenter.menu.shippingAddress'),
          link: '/collection/address',
          icon: 'icon-spiritling-dingwei',
        },
      ],
    },
  ]
})

const flatMenuItems = computed<FlatMenuItem[]>(() => {
  const menuItems: FlatMenuItem[] = []

  mallUserMenuGroups.value.forEach((group) => {
    group.items.forEach((item) => {
      menuItems.push({
        ...item,
        depth: 0,
      })

      item.children?.forEach((child) => {
        menuItems.push({
          ...child,
          depth: 1,
          parentKey: item.key,
        })
      })
    })
  })

  return menuItems
})

function normalizeMenuPath(path: string): string {
  const value = String(path || '').trim()
  if (!value) {
    return '/'
  }

  const userCenterPathIndex = value.indexOf('/user-center/')
  const menuPathValue = userCenterPathIndex > 0
    ? value.slice(userCenterPathIndex)
    : value
  const normalizedValue = menuPathValue.startsWith('/user-center/')
    ? (menuPathValue.replace(/^\/user-center/, '') || '/')
    : menuPathValue

  const [pathname] = normalizedValue.split('?')
  return (pathname || '/').replace(/\/+$/, '') || '/'
}

const normalizedCurrentPath = computed(() => {
  return normalizeMenuPath(String(route.path || ''))
})

watch(
  [
    normalizedCurrentPath,
    userCenterRedirectMap,
    isCollectionUserCenter,
  ],
  () => {
    const redirectPath = resolveUserCenterRedirectPath(userCenterRedirectMap.value, route.fullPath)
    if (redirectPath) {
      if (isExternalRedirectPath(redirectPath)) {
        window.location.replace(redirectPath)
        return
      }

      router.replace(redirectPath).catch(() => {})
      return
    }

    if (!isCollectionUserCenter.value || normalizedCurrentPath.value !== MALL_USER_CENTER_HOME_PATH) {
      return
    }

    router.replace(COLLECTION_USER_CENTER_HOME_PATH).catch(() => {})
  },
  { immediate: true },
)

function getPathMatchScore(item: MallMenuItem): number {
  const linkPath = normalizeMenuPath(item.link)
  const activePaths = (item.activePaths || []).map(path => normalizeMenuPath(path))
  const candidatePaths = [linkPath, ...activePaths]

  let score = 0

  candidatePaths.forEach((path) => {
    if (normalizedCurrentPath.value === path) {
      score = Math.max(score, path === linkPath ? 1000 : 900)
      return
    }

    if (normalizedCurrentPath.value.startsWith(`${path}/`)) {
      score = Math.max(score, path === linkPath ? 100 : 90)
    }
  })

  return score
}

function isQueryMatched(item: MallMenuItem): boolean {
  if (!item.activeQueries || !Object.keys(item.activeQueries).length) {
    return true
  }

  return Object.entries(item.activeQueries).every(([key, expectedValues]) => {
    const currentValue = route.query[key]
    const currentValues = Array.isArray(currentValue)
      ? currentValue.map(value => String(value || '').trim())
      : [String(currentValue || '').trim()]

    return currentValues.some(value => expectedValues.includes(value))
  })
}

function getMenuMatchScore(item: FlatMenuItem): number {
  let score = getPathMatchScore(item) + item.depth * 10

  if (item.activeQueries && Object.keys(item.activeQueries).length) {
    score += 100
  }

  return score
}

const activeMenuItem = computed<FlatMenuItem | null>(() => {
  const matchedItems = flatMenuItems.value.filter(item => getPathMatchScore(item) > 0 && isQueryMatched(item))

  if (!matchedItems.length) {
    return null
  }

  return matchedItems.reduce((bestItem, currentItem) => {
    return getMenuMatchScore(currentItem) > getMenuMatchScore(bestItem)
      ? currentItem
      : bestItem
  })
})

const activeMenuKey = computed(() => {
  return activeMenuItem.value?.key || ''
})

const activeBranchKey = computed(() => {
  return activeMenuItem.value?.parentKey || activeMenuItem.value?.key || ''
})

const expandedParentKeys = ref<Record<string, boolean>>({})

watch(mallUserMenuGroups, (groups) => {
  const nextExpandedKeys = { ...expandedParentKeys.value }

  groups.forEach((group) => {
    group.items.forEach((item) => {
      if (item.children?.length && nextExpandedKeys[item.key] === undefined) {
        nextExpandedKeys[item.key] = true
      }
    })
  })

  expandedParentKeys.value = nextExpandedKeys
}, { immediate: true })

watch(activeBranchKey, (key) => {
  if (!key) {
    return
  }

  if (expandedParentKeys.value[key] === false) {
    expandedParentKeys.value = {
      ...expandedParentKeys.value,
      [key]: true,
    }
  }
}, { immediate: true })

function isMenuItemActive(item: MallMenuItem): boolean {
  return activeMenuKey.value === item.key
}

function hasMenuChildren(item: MallMenuItem): boolean {
  return Boolean(item.children?.length)
}

function isMenuBranchExpanded(item: MallMenuItem): boolean {
  return expandedParentKeys.value[item.key] !== false
}

function toggleMenuBranch(itemKey: string) {
  expandedParentKeys.value = {
    ...expandedParentKeys.value,
    [itemKey]: !(expandedParentKeys.value[itemKey] !== false),
  }
}

function getSidebarIconSrc(iconName?: string): string {
  if (!iconName) {
    return ''
  }

  return mallHomeSidebarIconSrcMap[iconName] || ''
}

function goToPage(path: string) {
  if (!path || route.fullPath === path) {
    return
  }

  router.push(path).catch(() => {})
}

function getStoredSidebarScrollTop() {
  if (typeof window === 'undefined') {
    return sidebarScrollTopMemory
  }

  const storedValue = Number(window.sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY) || 0)
  if (Number.isFinite(storedValue) && storedValue >= 0) {
    sidebarScrollTopMemory = storedValue
  }

  return sidebarScrollTopMemory
}

function setStoredSidebarScrollTop(scrollTop: number) {
  const nextScrollTop = Math.max(Number(scrollTop || 0), 0)
  sidebarScrollTopMemory = nextScrollTop

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, String(nextScrollTop))
  }
}

function handleSidebarScroll({ scrollTop }: { scrollTop: number }) {
  setStoredSidebarScrollTop(scrollTop)
}

function restoreSidebarScrollTop() {
  const scrollbar = sidebarScrollbarRef.value
  const targetScrollTop = getStoredSidebarScrollTop()

  if (!scrollbar || !targetScrollTop) {
    return
  }

  if (typeof scrollbar.setScrollTop === 'function') {
    scrollbar.setScrollTop(targetScrollTop)
    return
  }

  const wrapRef = scrollbar.wrapRef
  if (wrapRef) {
    wrapRef.scrollTop = targetScrollTop
  }
}

watch(
  () => route.fullPath,
  () => {
    nextTick(() => {
      restoreSidebarScrollTop()
    })
  },
  { immediate: true },
)
</script>

<template>
  <div
    class="layout-qiankun-admin"
    :class="{
      'layout-long-locale-wrap': isLongTextLocale,
      'w-full!': isFullLayout,
    }"
  >
    <el-container>
      <div
        v-if="!hiddenSider"
        class="layout-qiankun-admin__sidebar-column"
        :style="{ width: asideWidth }"
      >
        <el-aside
          width="100%"
          class="layout-qiankun-admin__aside"
        >
          <el-scrollbar
            ref="sidebarScrollbarRef"
            class="nav-user-sider-wrap"
            @scroll="handleSidebarScroll"
          >
            <div
              v-for="(group, groupIndex) in mallUserMenuGroups"
              :key="`${group.title}-${groupIndex}`"
              class="nav-user-group"
            >
              <div v-if="group.title" class="nav-user-group__title">
                {{ group.title }}
              </div>

              <div
                v-for="item in group.items"
                :key="item.key"
                class="nav-user-branch"
                :class="{ 'is-collapsed': hasMenuChildren(item) && !isMenuBranchExpanded(item) }"
              >
                <div
                  v-if="hasMenuChildren(item)"
                  class="nav-user-parent-row"
                >
                  <button
                    type="button"
                    class="nav-user-item nav-user-item--parent nav-user-item--parent-main"
                    :class="{ 'is-active': isMenuItemActive(item) }"
                    @click="goToPage(item.link)"
                  >
                    <img
                      v-if="getSidebarIconSrc(item.icon)"
                      :src="getSidebarIconSrc(item.icon)"
                      alt=""
                      aria-hidden="true"
                      class="nav-user-item__icon-image"
                    >
                    <i
                      v-else-if="item.icon"
                      class="icon-font"
                      :class="{ [item.icon]: true }"
                    />
                    <span>{{ item.name }}</span>
                  </button>

                  <button
                    type="button"
                    class="nav-user-branch-toggle"
                    :class="{
                      'is-expanded': isMenuBranchExpanded(item),
                    }"
                    @click.stop="toggleMenuBranch(item.key)"
                  >
                    <span class="nav-user-branch-toggle__icon" />
                  </button>
                </div>

                <button
                  v-else
                  type="button"
                  class="nav-user-item nav-user-item--parent"
                  :class="{ 'is-active': isMenuItemActive(item) }"
                  @click="goToPage(item.link)"
                >
                  <img
                    v-if="getSidebarIconSrc(item.icon)"
                    :src="getSidebarIconSrc(item.icon)"
                    alt=""
                    aria-hidden="true"
                    class="nav-user-item__icon-image"
                  >
                  <i
                    v-else-if="item.icon"
                    class="icon-font"
                    :class="{ [item.icon]: true }"
                  />
                  <span>{{ item.name }}</span>
                </button>

                <div
                  v-if="hasMenuChildren(item) && isMenuBranchExpanded(item)"
                  class="nav-user-children"
                >
                  <button
                    v-for="child in item.children"
                    :key="child.key"
                    type="button"
                    class="nav-user-item nav-user-item--child"
                    :class="{ 'is-active': isMenuItemActive(child) }"
                    @click="goToPage(child.link)"
                  >
                    <span>{{ child.name }}</span>
                  </button>
                </div>
              </div>
            </div>
          </el-scrollbar>
        </el-aside>

        <button
          v-if="showCollectionTaskBanner"
          type="button"
          class="collection-task-banner"
          :aria-label="$t('userCenter.newCustomerTask.centerTitle')"
          @click="goToPage('/collection/newCustomerTask')"
        >
          <img
            :src="newCustomerTaskBanner"
            alt=""
            aria-hidden="true"
          >
        </button>
      </div>

      <el-main v-if="!hiddenSider" class="layout-qiankun-admin__main">
        <slot />
      </el-main>

      <div v-else class="w-full">
        <slot />
      </div>
    </el-container>
  </div>
</template>

<style lang="scss">
@use "../src/styles/qiankunmall-style.scss";
</style>

<style lang="scss" scoped>
.layout-qiankun-admin {
  --shell-page-bg: #fbfaf9;
  --shell-surface: #fff;
  --shell-surface-soft: #fff5ed;
  --shell-border: rgba(232, 229, 229, 0.72);
  --shell-brand: #ff7228;
  --shell-text-primary: #1a1817;
  --shell-text-secondary: #645a56;
  --shell-text-disabled: #b6adaa;
  --shell-shadow: 0 1px 2px rgba(26, 24, 23, 0.05);
  --primary: var(--shell-brand);
  --el-color-primary-rgb: 255, 114, 40;
  --el-color-primary: var(--shell-brand);
  --el-color-primary-light-3: #ff9c69;
  --el-color-primary-light-5: #ffb998;
  --el-color-primary-light-7: #ffd4c3;
  --el-color-primary-light-8: #ffe3d7;
  --el-color-primary-light-9: #fff3eb;
  --el-color-primary-dark-2: #e76424;
  --el-border-color-hover: var(--shell-brand);
  --sidebar-max-height: calc(100vh - 48px);
  min-width: 1200px;
  margin: 0 auto;
  padding: 24px 24px 40px;

  :deep(.el-container) {
    align-items: flex-start;
    gap: 24px;
  }
}

.layout-qiankun-admin__sidebar-column {
  position: sticky;
  top: calc(66px + 24px);
  align-self: flex-start;
  flex: 0 0 auto;
}

.layout-qiankun-admin__aside {
  max-height: var(--sidebar-max-height);
  background: var(--shell-surface);
  border: 1px solid var(--shell-border);
  border-radius: 12px;
  box-shadow: var(--shell-shadow);
  overflow: hidden;
}

.layout-qiankun-admin__main {
  flex: 1;
  min-width: 0;
  padding-top: var(--el-main-padding);
  padding-left: 0;
  padding-right: 0;
}

:deep(.layout-qiankun-admin__main) .org-page-wrap {
  background: var(--shell-surface);
  padding-top: 1px;
  margin-top: -18px;

  border: 1px solid var(--shell-border);
  border-radius: 12px;
  box-shadow: var(--shell-shadow);
}

:deep(.copyText-container) {
  position: absolute;
  z-index: -99999;
  left: -9999999px;
}
.nav-user-sider-wrap {
  width: 100%;
  height: auto;
  max-height: var(--sidebar-max-height);

  :deep(.el-scrollbar__wrap) {
    max-height: var(--sidebar-max-height);
  }

  :deep(.el-scrollbar__view) {
    display: flex;
    min-height: 100%;
    flex-direction: column;
    padding: 12px;
    box-sizing: border-box;
  }
}

.nav-user-group + .nav-user-group {
  margin-top: 12px;
}

.collection-task-banner {
  width: 100%;
  margin: 12px 0 0;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
}

.nav-user-group__title {
  margin: 8px 12px;
  color: var(--shell-text-disabled);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}

.nav-user-branch + .nav-user-branch {
  margin-top: 4px;
}

.nav-user-parent-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.nav-user-item {
  position: relative;
  width: 100%;
  align-items: center;
  display: flex;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  min-height: 40px;
  padding: 0 12px;
  background: transparent;
  color: var(--shell-text-secondary);
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 114, 40, 0.06);
  }

  .icon-font {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    min-width: 16px;
    height: 16px;
    color: #8b7f7a;
    font-size: 16px;
    line-height: 1;
    text-align: center;
    transform-origin: center;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.is-active {
    background: rgba(255, 114, 40, 0.08);
    color: var(--shell-brand);
    font-weight: 500;
  }

  &.is-active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    border-radius: 999px;
    background: var(--shell-brand);
  }
}

.nav-user-item__icon-image {
  display: inline-flex;
  width: 16px;
  min-width: 16px;
  height: 16px;
  object-fit: contain;
}

.nav-user-item--parent {
  justify-content: flex-start;

  .icon-priority-purchase{
    transform: scale(0.85);
  }
  .icon-menu-user-center {
    transform: scale(1.2);
  }

  .icon-time {
    transform: scale(0.9);
  }

  .icon-collect2 {
    transform: scale(1.3);
  }

  .icon-Wallet {
    transform: scale(1.2);
  }

  .icon-order {
    transform: scale(1.3);
  }

  .icon-Warehouse {
    transform: scale(1.35);
  }

  .icon-parcel {
    transform: scale(1.25);
  }

  .icon-submitParcels {
    transform: scale(1.14);
  }

  .icon-order-coupon {
    transform: scale(1.2);
  }

  .icon-liwuhuodong {
    transform: scale(0.88);
  }

  .icon-question1 {
    transform: scale(0.9);
  }

  .icon-spiritling-dingwei {
    transform: scale(0.86);
  }
}

.nav-user-item--parent-main {
  flex: 1;
  min-width: 0;
  justify-content: flex-start;
}

.nav-user-branch-toggle {
  width: 32px;
  min-width: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--shell-text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 114, 40, 0.06);
    color: var(--shell-brand);
  }
}

.nav-user-branch-toggle__icon {
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.nav-user-branch-toggle.is-expanded .nav-user-branch-toggle__icon {
  transform: rotate(225deg);
}

.nav-user-children {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0 8px 12px;
}

.nav-user-item--child {
  color: var(--shell-text-secondary);
  font-size: 12px;
  font-weight: 400;
  justify-content: space-between;
  min-height: 36px;
  padding: 7px 12px 7px 24px;
  line-height: 18px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 114, 40, 0.06);
  }

  &.is-active {
    background: var(--shell-surface-soft);
    color: var(--shell-brand);
    font-weight: 500;
  }

  &.is-active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 2px;
    height: 100%;
    border-radius: 999px;
    background: var(--shell-brand);
  }
}

.layout-long-locale-wrap {
  .nav-user-item {
    font-size: 11px;
  }

  .nav-user-item .icon-font {
    margin-top: 1px;
  }

  .nav-user-item--child {
    font-size: 11px;
  }
}

@media (max-width: 1280px) {
  .layout-qiankun-admin {
    width: auto;
    padding: 16px 16px 32px;
  }
}

@media (max-width: 960px) {
  .layout-qiankun-admin {
    --sidebar-max-height: none;

    :deep(.el-container) {
      flex-direction: column;
      gap: 16px;
    }
  }

  .layout-qiankun-admin__aside {
    position: static;
    max-height: none;
    width: 100% !important;
  }

  .layout-qiankun-admin__sidebar-column {
    position: static;
    width: 100% !important;
  }
}
</style>
